/* eslint-disable no-underscore-dangle */
import 'app/styles/style.scss';
import 'react-datepicker/dist/react-datepicker.css';
import './style.css';

import React from 'react';
import * as _ from 'lodash';
import { FormattedMessage, injectIntl, IntlShape, WrappedComponentProps } from 'react-intl';
import Select, { ActionMeta, InputActionMeta, StylesConfig, ValueType } from 'react-select';
import Creatable from 'react-select/creatable';
// @ts-ignore - react-datepicker doesn't have types in this project
import ReactDatePicker from 'react-datepicker';
import produce from 'immer';
import { RegisterOptions } from 'react-hook-form';

import {
  SPECIES,
  makeSpeciesOptions,
  makeRacesOptions,
  DEFAULT_CAT_RACE,
  SpeciesType,
} from 'app/constants/species';
import { SEXES, makeSexesOptions, SEXES_WITH_OTHER } from 'app/constants/sexes';
import ApiCalls from 'app/utils/apiCalls';

import PatientConflictWarning from 'app/components/PatientConflictWarning';
import OptionWithPatientData from 'app/components/OptionWithPatientData';
import ToolsDropdownIndicator from 'app/components/ToolsBar/ToolsDropdownIndicator';
import AgeFromDate from 'app/components/AgeFromDate';
import { DARK_INPUT_STYLE } from 'app/ReactSelectStyles';
import AttendingVeterinarianSelector from 'app/components/AttendingVeterinarianSelector';
import { Patient } from 'app/interfaces/Patient';
import { Form, FormField } from 'semantic-ui-react';

// Type definitions
type SexType = typeof SEXES_WITH_OTHER[number];

// Field keys matching PatientInfoOutput
export type PatientFieldKey =
  | 'name'
  | 'owner_name'
  | 'sex'
  | 'birth_date'
  | 'specie'
  | 'race'
  | 'weight'
  | 'file_id'
  | 'chip_id'
  | 'pedigree_id'
  | 'attending_veterinarian';
type PatientFieldEntry = PatientFieldKey | PatientFieldKey[];

// Validation rules per field using react-hook-form format
export type FieldRules = Partial<Record<PatientFieldKey, RegisterOptions>>;

// Layout as rows of field keys
export type FieldLayout = PatientFieldEntry[][];

// Default layout showing all fields
const DEFAULT_FIELD_LAYOUT: FieldLayout = [
  ['file_id', 'owner_name', 'name'],
  ['chip_id', 'specie', 'birth_date'],
  ['pedigree_id', 'race', ['sex', 'attending_veterinarian']],
];

interface SelectOption<T = string> {
  label: string | React.ReactNode;
  value: T;
  data?: PatientData;
}

interface PatientData extends Patient {
  owner_name?: string;
}

export interface PatientInfoOutput {
  file_id?: string;
  chip_id?: string;
  pedigree_id?: string;
  animal_id?: string;
  name?: string;
  owner_name?: string;
  owner?: string;
  attending_veterinarian?: string;
  birth_date?: Date;
  specie?: SpeciesType;
  race?: string;
  sex?: string;
  weight?: number;
}

export interface OnChangePayload {
  patientInfo: PatientInfoOutput;
  hasConflict: boolean;
  missingRequiredFields: PatientFieldKey[];
}

interface CreatableFieldState {
  backendField: string;
  selectedOption: SelectOption | null;
  inputValue: string;
  options: SelectOption[];
  onInputFocus: () => void;
  onInputChange: (value: string, actionMeta: InputActionMeta) => void;
  loadOptions: (value: string) => void;
  getOptionsFromServer: _.DebouncedFunc<
    (stateFieldKey: AutocompleteFieldKey, data: Record<string, unknown>) => Promise<void>
  >;
  onSelect: (value: ValueType<SelectOption, false>, actionMeta: ActionMeta<SelectOption>) => void;
  components?: Record<string, React.ComponentType<any> | null>;
  conflictingAnimals?: PatientData[];
}

interface RaceFieldState {
  options: SelectOption[];
  selectedOption: SelectOption | null;
}

interface BirthDateFieldState {
  value?: Date;
}

type AutocompleteFieldKey = 'fileID' | 'chipID' | 'pedigreeID' | 'owner' | 'animal';
type FieldKeyWithSelectedOptions = AutocompleteFieldKey | 'race';
type FieldKeyWithValue = 'birthDate' | 'specie' | 'sex' | 'attendingVeterinarian' | 'weight';

interface PatientInfoFormState {
  fileID: CreatableFieldState;
  chipID: CreatableFieldState;
  owner: CreatableFieldState;
  animal: CreatableFieldState;
  pedigreeID: CreatableFieldState;
  race: RaceFieldState;
  birthDate: BirthDateFieldState;
  specie: SelectOption<SpeciesType> | null;
  sex: SelectOption<string> | null;
  attendingVeterinarian?: string;
  weight?: string;
  animalId?: string;
  hasConflict: boolean;
}

interface PatientInfoFormProps extends WrappedComponentProps {
  onChange: (payload: OnChangePayload) => void;
  className?: string;
  initialPatientData?: PatientData;
  menuPlacement?: 'auto' | 'top' | 'bottom';
  fieldRules?: FieldRules;
  fieldLayout?: FieldLayout;
  fieldErrors?: Partial<Record<keyof PatientInfoOutput, string>>;
}

// Constants
const ON_CHANGE_ACTIONS = ['clear', 'select-option', 'create-option'];

const STATE_FIELD_WITH_SELECTED_OPTIONS: FieldKeyWithSelectedOptions[] = [
  'fileID',
  'chipID',
  'pedigreeID',
  'owner',
  'animal',
  'race',
];
const STATE_FIELD_WITH_VALUE: FieldKeyWithValue[] = [
  'birthDate',
  'specie',
  'sex',
  'attendingVeterinarian',
  'weight',
];

const textSelectStyle: StylesConfig<SelectOption, false> = _.merge(
  {},
  _.cloneDeep(DARK_INPUT_STYLE),
  {
    container: (provided: React.CSSProperties, ...args: unknown[]) => ({
      ...(DARK_INPUT_STYLE as any).container(provided, ...args),
      minWidth: '10ch',
    }),
  }
);

const sexStyles: StylesConfig<SelectOption, false> = _.merge({}, _.cloneDeep(DARK_INPUT_STYLE), {
  container: (provided: React.CSSProperties, ...args: unknown[]) => ({
    ...(DARK_INPUT_STYLE as any).container(provided, ...args),
    width: 'fit-content',
  }),
  control: (provided: React.CSSProperties, ...args: unknown[]) => ({
    ...(DARK_INPUT_STYLE as any).control(provided, ...args),
    width: '32px',
    paddingLeft: '0',
  }),
  option: (provided: React.CSSProperties, ...args: unknown[]) => ({
    ...(DARK_INPUT_STYLE as any).option(provided, ...args),
    padding: '5px',
  }),
  input: (provided: React.CSSProperties) => ({ ...provided, color: 'transparent' }),
  singleValue: (provided: React.CSSProperties, ...args: unknown[]) => ({
    ...(DARK_INPUT_STYLE as any).singleValue(provided, ...args),
    transform: 'translateY(-50%) translateX(-50%)',
    left: '50%',
    top: '50%',
    margin: '0 !important',
  }),
});

function injectErrorInStyles(
  styles: StylesConfig<SelectOption, false>,
  error?: string
): StylesConfig<SelectOption, false> {
  if (!error) return styles;
  return _.merge({}, styles, {
    container: (provided: React.CSSProperties, ...args: unknown[]) => ({
      ...(DARK_INPUT_STYLE as any).container(provided, ...args),
      border: '1px solid',

      borderColor: 'var(--dark-mode-color--red)',
      color: 'var(--dark-mode-color--red)',
      backgroundColor: '#fff6f6',
    }),
  });
}

// Helper functions
const formatFieldFromData = (
  fieldKey: keyof PatientData,
  animalData?: PatientData
): SelectOption | null =>
  animalData && animalData[fieldKey]
    ? {
        label: String(animalData[fieldKey]),
        value: String(animalData[fieldKey]),
        data: animalData,
      }
    : null;

const formatPatientInfoData = (state: PatientInfoFormState): PatientInfoOutput =>
  _.omitBy(
    {
      file_id: _.get(state, 'fileID.selectedOption.value'),
      chip_id: _.get(state, 'chipID.selectedOption.value'),
      pedigree_id: state.pedigreeID?.selectedOption?.value,
      animal_id: state.animalId,
      name: _.get(state, 'animal.selectedOption.value'),
      owner_name: _.get(state, 'owner.selectedOption.value'),
      owner: _.get(state, 'owner.selectedOption.data.owner'),
      attending_veterinarian: state.attendingVeterinarian,
      birth_date: _.get(state, 'birthDate.value'),
      specie: _.get(state, 'specie.value'),
      race: _.get(state, 'race.selectedOption.value'),
      sex: _.get(state, 'sex.value'),
      weight: state.weight ? parseFloat(state.weight) : undefined,
    },
    _.isUndefined
  ) as PatientInfoOutput;

const getMissingRequiredFields = (
  patientInfo: PatientInfoOutput,
  fieldRules?: FieldRules
): PatientFieldKey[] => {
  if (!fieldRules) return [];
  const missingFields: PatientFieldKey[] = [];
  (Object.keys(fieldRules) as PatientFieldKey[]).forEach((fieldKey) => {
    if (fieldRules[fieldKey]?.required && !patientInfo[fieldKey]) {
      missingFields.push(fieldKey);
    }
  });
  return missingFields;
};

const isOptionDataSelected = (
  option: SelectOption,
  selectedOptions: readonly SelectOption[]
): boolean => _.some(selectedOptions, { data: option.data });

const hasConflicts = (state: PatientInfoFormState): boolean => _.some(state, 'conflictingAnimals');

const getConflictingFields = (
  state: PatientInfoFormState,
  conflictingAnimalData: PatientData
): (keyof PatientInfoFormState)[] => {
  const formattedStatePatientData = formatPatientInfoData(state);
  const isFileIDEqual = formattedStatePatientData.file_id === conflictingAnimalData.file_id;
  const isChipIDEqual = formattedStatePatientData.chip_id === conflictingAnimalData.chip_id;
  const isAnimalEqual =
    formattedStatePatientData.name === conflictingAnimalData.name &&
    formattedStatePatientData.owner_name === conflictingAnimalData.owner_name;

  const conflictingFields: (keyof PatientInfoFormState)[] = [];
  if (isFileIDEqual) conflictingFields.push('fileID');
  if (isChipIDEqual) conflictingFields.push('chipID');
  if (isAnimalEqual) conflictingFields.push('owner', 'animal');
  return conflictingFields;
};

class PatientInfoForm extends React.PureComponent<PatientInfoFormProps, PatientInfoFormState> {
  private getAnimalOptionsFromServer: (
    stateFieldKey: AutocompleteFieldKey,
    data: Record<string, unknown>
  ) => Promise<void>;

  private getOwnerOptionsFromServer: (
    stateFieldKey: AutocompleteFieldKey,
    data: Record<string, unknown>
  ) => Promise<void>;

  private previousState: PatientInfoFormState;

  constructor(props: PatientInfoFormProps) {
    super(props);

    this.getAnimalOptionsFromServer = this.getOptionsFromServer.bind(
      this,
      ApiCalls.getAnimalNameCompletion,
      'animals'
    );

    this.getOwnerOptionsFromServer = this.getOptionsFromServer.bind(
      this,
      ApiCalls.getOwnerNameCompletion,
      'owners'
    );

    this.state = this.createInitialState(this.props);
    this.previousState = this.state;
    this.refreshOptions();
  }

  componentDidMount(): void {
    const { onChange, fieldRules } = this.props;
    const patientInfo = formatPatientInfoData(this.state);
    const missingRequiredFields = getMissingRequiredFields(patientInfo, fieldRules);

    onChange({ patientInfo, hasConflict: false, missingRequiredFields });
  }

  componentDidUpdate(prevProps: PatientInfoFormProps, prevState: PatientInfoFormState): void {
    const { onChange } = this.props;
    const { animalId } = this.state;
    const hasFieldChanged =
      _.some(
        STATE_FIELD_WITH_SELECTED_OPTIONS,
        (stateFieldKey) =>
          (this.state[stateFieldKey] as CreatableFieldState | RaceFieldState).selectedOption !==
          (prevState[stateFieldKey] as CreatableFieldState | RaceFieldState).selectedOption
      ) ||
      _.some(
        STATE_FIELD_WITH_VALUE,
        (stateFieldKey) => this.state[stateFieldKey] !== prevState[stateFieldKey]
      );
    const hasConflictNow = hasConflicts(this.state);
    const hasConflictPrevious = hasConflicts(prevState);

    const isAnimalIDUpdate = animalId !== prevState.animalId;
    if ((hasFieldChanged || isAnimalIDUpdate) && !hasConflictPrevious) {
      this.previousState = produce(prevState, (draftState) => {
        _.forEach(STATE_FIELD_WITH_SELECTED_OPTIONS, (fieldKey) => {
          const field = draftState[fieldKey] as CreatableFieldState;
          if (field.inputValue !== undefined) {
            field.inputValue = '';
          }
        });
      });
    }
    if (hasFieldChanged || hasConflictNow !== hasConflictPrevious || isAnimalIDUpdate) {
      const { fieldRules } = this.props;
      const patientInfo = formatPatientInfoData(this.state);
      const missingRequiredFields = getMissingRequiredFields(patientInfo, fieldRules);
      onChange({ patientInfo, hasConflict: hasConflictNow, missingRequiredFields });
    }
  }

  createInitialState = (props: PatientInfoFormProps): PatientInfoFormState => {
    const { initialPatientData } = props;
    return {
      fileID: {
        backendField: 'file_id',
        selectedOption: formatFieldFromData('file_id', initialPatientData),
        inputValue: '',
        options: [],
        onInputFocus: this.onInputFocus.bind(this, 'fileID'),
        onInputChange: this.onInputChange.bind(this, 'fileID'),
        loadOptions: (value: string) => this.loadOptions('fileID', { file_id: value }),
        getOptionsFromServer: _.debounce(this.getAnimalOptionsFromServer, 300),
        onSelect: this.onSelectAnimal.bind(this, 'fileID'),
        components: {
          DropdownIndicator: ToolsDropdownIndicator,
          IndicatorSeparator: null,
          Option: OptionWithPatientData('file_id'),
        },
        conflictingAnimals: undefined,
      },
      chipID: {
        backendField: 'chip_id',
        selectedOption: formatFieldFromData('chip_id', initialPatientData),
        inputValue: '',
        options: [],
        onInputFocus: this.onInputFocus.bind(this, 'chipID'),
        onInputChange: this.onInputChange.bind(this, 'chipID'),
        loadOptions: (value: string) => this.loadOptions('chipID', { chip_id: value }),
        getOptionsFromServer: _.debounce(this.getAnimalOptionsFromServer, 300),
        onSelect: this.onSelectAnimal.bind(this, 'chipID'),
        components: {
          DropdownIndicator: ToolsDropdownIndicator,
          IndicatorSeparator: null,
          Option: OptionWithPatientData('chip_id'),
        },
        conflictingAnimals: undefined,
      },
      owner: {
        backendField: 'name',
        selectedOption: formatFieldFromData('owner_name', initialPatientData),
        inputValue: '',
        options: [],
        onInputFocus: this.onInputFocus.bind(this, 'owner'),
        onInputChange: this.onInputChange.bind(this, 'owner'),
        loadOptions: (value: string) =>
          this.loadOptions('owner', this.formatOwnerSearchData(value)),
        getOptionsFromServer: _.debounce(this.getOwnerOptionsFromServer, 300),
        onSelect: this.onSelectOwner,
        conflictingAnimals: undefined,
      },
      animal: {
        backendField: 'name',
        selectedOption: formatFieldFromData('name', initialPatientData),
        inputValue: '',
        options: [],
        onInputFocus: this.onInputFocus.bind(this, 'animal'),
        onInputChange: this.onInputChange.bind(this, 'animal'),
        loadOptions: (value: string) =>
          this.loadOptions('animal', this.formatAnimalSearchData(value)),
        getOptionsFromServer: _.debounce(this.getAnimalOptionsFromServer, 300),
        onSelect: this.onSelectAnimal.bind(this, 'animal'),
        components: {
          DropdownIndicator: ToolsDropdownIndicator,
          IndicatorSeparator: null,
          Option: OptionWithPatientData('name'),
        },
        conflictingAnimals: undefined,
      },
      pedigreeID: {
        backendField: 'pedigree_id',
        selectedOption: formatFieldFromData('pedigree_id', initialPatientData),
        inputValue: '',
        options: [],
        onInputFocus: this.onInputFocus.bind(this, 'pedigreeID'),
        onInputChange: this.onInputChange.bind(this, 'pedigreeID'),
        loadOptions: (value: string) => this.loadOptions('pedigreeID', { pedigree_id: value }),
        getOptionsFromServer: _.debounce(this.getAnimalOptionsFromServer, 300),
        onSelect: this.onSelectAnimal.bind(this, 'pedigreeID'),
        components: {
          DropdownIndicator: ToolsDropdownIndicator,
          IndicatorSeparator: null,
          Option: OptionWithPatientData('pedigree_id'),
        },
      },
      attendingVeterinarian: initialPatientData?.attending_veterinarian,
      birthDate: {
        value: initialPatientData?.birth_date,
      },
      specie: this.makeSpecieField(initialPatientData?.specie),
      race: this.makeRaceField(initialPatientData?.specie, initialPatientData?.race),
      sex: this.makeSexField(initialPatientData?.sex),
      weight: initialPatientData?.weight?.toString(),

      animalId: initialPatientData?._id,
      hasConflict: false,
    };
  };

  clearConflicts = (): void =>
    this.setState(
      produce((draftState: PatientInfoFormState) => {
        _.forEach(draftState, (stateField: any) => {
          if (stateField?.conflictingAnimals !== undefined) {
            stateField.conflictingAnimals = undefined;
          }
        });
      })
    );

  solveConflictsForCreate = (conflictingAnimals: PatientData[]): void => {
    this.setState(
      produce((draftState: PatientInfoFormState) => {
        const conflictingFields = getConflictingFields(draftState, conflictingAnimals[0]);
        conflictingFields.forEach((conflictingField) => {
          (draftState[conflictingField] as CreatableFieldState).selectedOption = null;
        });
      })
    );
    this.clearConflicts();
  };

  solveConflictsForUpdate = (conflictingAnimals: PatientData[]): void => {
    this.setState({
      animalId: conflictingAnimals[0]._id,
    });
    this.clearConflicts();
  };

  refreshOptions = (): void => {
    const fieldsToRefresh: AutocompleteFieldKey[] = [
      'fileID',
      'chipID',
      'pedigreeID',
      'animal',
      'owner',
    ];
    _.forEach(_.pick(this.state, fieldsToRefresh), (fieldState: CreatableFieldState) =>
      fieldState.loadOptions(_.get(fieldState, 'selectedOption.value', ''))
    );
  };

  onSelectAnimal = (
    stateFieldKey: AutocompleteFieldKey,
    value: ValueType<SelectOption, false>,
    { action }: ActionMeta<SelectOption>
  ): void => {
    const refreshOptionsIfNeeded = (): void => {
      const shouldRefreshOwnerOptions = stateFieldKey === 'animal';
      if (shouldRefreshOwnerOptions) this.refreshOwnerOptions();
    };

    if (action === 'clear') {
      this.setState(
        produce((draftState: PatientInfoFormState) => {
          (draftState[stateFieldKey] as CreatableFieldState).inputValue = '';
          (draftState[stateFieldKey] as CreatableFieldState).selectedOption = null;
          draftState.animalId = undefined;
        }),
        () => {
          refreshOptionsIfNeeded();
          this.checkConflicts(stateFieldKey);
        }
      );
      const fieldState = this.state[stateFieldKey] as CreatableFieldState;
      fieldState.loadOptions('');
    } else if (action === 'create-option' && value) {
      this.setState(
        produce((draftState: PatientInfoFormState) => {
          (draftState[stateFieldKey] as CreatableFieldState).selectedOption = value;
          (draftState[stateFieldKey] as CreatableFieldState).inputValue = value.value;
        }),
        () => this.checkConflicts(stateFieldKey)
      );
    } else if (action === 'select-option' && value?.data) {
      const { _id, birth_date, sex, specie, race } = value.data;
      this.setState(
        produce((draftState: PatientInfoFormState) => {
          (draftState[stateFieldKey] as CreatableFieldState).inputValue = value.value;
          draftState.fileID.selectedOption = formatFieldFromData('file_id', value.data);
          if (draftState.fileID.selectedOption) {
            draftState.fileID.options = [draftState.fileID.selectedOption];
          }

          draftState.chipID.selectedOption = formatFieldFromData('chip_id', value.data);
          if (draftState.chipID.selectedOption) {
            draftState.chipID.options = [draftState.chipID.selectedOption];
          }

          draftState.owner.selectedOption = formatFieldFromData('owner_name', value.data);
          if (draftState.owner.selectedOption) {
            draftState.owner.options = [draftState.owner.selectedOption];
          }

          draftState.animal.selectedOption = formatFieldFromData('name', value.data);
          if (draftState.animal.selectedOption) {
            draftState.animal.options = [draftState.animal.selectedOption];
          }
          draftState.pedigreeID.selectedOption = formatFieldFromData('pedigree_id', value.data);
          if (draftState.pedigreeID.selectedOption) {
            draftState.pedigreeID.options = [draftState.pedigreeID.selectedOption];
          }
          draftState.attendingVeterinarian = value.data?.attending_veterinarian;

          draftState.birthDate.value = birth_date;
          draftState.specie = this.makeSpecieField(specie);
          draftState.race = this.makeRaceField(specie, race);
          draftState.sex = this.makeSexField(sex);

          draftState.animalId = _id;
        }),
        refreshOptionsIfNeeded
      );
      this.clearConflicts();
    }
  };

  onSelectOwner = (
    value: ValueType<SelectOption, false>,
    { action }: ActionMeta<SelectOption>
  ): void => {
    const setOwnerState = (newValue: SelectOption): void => {
      this.setState(
        produce((draftState: PatientInfoFormState) => {
          draftState.owner.inputValue = newValue.value;
          draftState.owner.selectedOption = newValue;
        }),
        () => {
          this.refreshAnimalOptions();
          this.checkConflicts('owner');
        }
      );
    };

    if (action === 'clear') {
      this.setState(
        produce((draftState: PatientInfoFormState) => {
          draftState.owner.selectedOption = null;
          draftState.owner.inputValue = '';
        }),
        () => {
          this.refreshOwnerOptions();
          this.refreshAnimalOptions();
          this.checkConflicts('owner');
        }
      );
    } else if (action === 'select-option' && value?.data) {
      // We have to reformat option to make data similar to data coming from
      // formatFieldFromData which is used when doing cross search with both animal_name
      // and owner_name.
      const ownerFormattedOption: SelectOption = {
        ...value,
        data: {
          owner: (value.data as any)._id,
          owner_name: (value.data as any).name,
        },
      };
      setOwnerState(ownerFormattedOption);
    } else if (action === 'create-option' && value) {
      setOwnerState(value);
    }
  };

  setInputValue = (key: AutocompleteFieldKey, inputValue: string): void =>
    this.setState(
      produce((draftState: PatientInfoFormState) => {
        (draftState[key] as CreatableFieldState).inputValue = inputValue;
      })
    );

  onInputFocus = (key: AutocompleteFieldKey): void => {
    const fieldState = this.state[key] as CreatableFieldState;
    if (fieldState.selectedOption) {
      this.setInputValue(key, fieldState.selectedOption.value);
    }
  };

  onInputChange = (key: AutocompleteFieldKey, value: string, { action }: InputActionMeta): void => {
    const fieldState = this.state[key] as CreatableFieldState;

    if (action === 'input-change') {
      fieldState.loadOptions?.(value);
      this.setState(
        produce((draftState: PatientInfoFormState) => {
          (draftState[key] as CreatableFieldState).inputValue = value;
          if (value === '') {
            (draftState[key] as CreatableFieldState).selectedOption = null;
          }
        })
      );
      if (key === 'owner') {
        this.refreshAnimalOptions();
      } else if (key === 'animal') {
        this.refreshOwnerOptions();
      }
    } else if (action === 'input-blur') {
      this.setState(
        produce((draftState: PatientInfoFormState) => {
          const { inputValue, selectedOption } = draftState[key] as CreatableFieldState;
          const noValueChange = selectedOption?.value === inputValue;
          (draftState[key] as CreatableFieldState).inputValue = '';
          if (!inputValue || noValueChange) return;
          (draftState[key] as CreatableFieldState).selectedOption = {
            value: inputValue,
            label: inputValue,
          };
          (draftState[key] as CreatableFieldState).inputValue = '';
          this.checkConflicts(key, draftState);
        })
      );
    }
  };

  formatAnimalSearchData = (partialAnimalName: string): Record<string, unknown> => {
    const { owner } = this.state;
    return {
      owner_id: _.get(owner, 'selectedOption.data.owner'),
      owner_name: _.get(owner, 'selectedOption.value'),
      animal_name: partialAnimalName,
    };
  };

  formatOwnerSearchData = (partialOwnerName: string): Record<string, unknown> => {
    const { animal } = this.state;
    return {
      owner_name: partialOwnerName,
      animal_name: _.get(animal, 'selectedOption.value'),
    };
  };

  refreshOwnerOptions = (): void => {
    const { owner } = this.state;
    owner.loadOptions(_.get(owner, 'selectedOption.value', ''));
  };

  refreshAnimalOptions = (): void => {
    const { animal } = this.state;
    animal.loadOptions(_.get(animal, 'selectedOption.value', ''));
  };

  checkConflicts = (
    stateFieldKey: AutocompleteFieldKey | 'attendingVeterinarian',
    initialCheckedState?: PatientInfoFormState
  ): void => {
    if (['pedigreeID', 'attendingVeterinarian'].includes(stateFieldKey)) return;
    const checkedState = initialCheckedState ?? this.state;
    ApiCalls.checkPatientInfoConflicts(formatPatientInfoData(checkedState)).then(
      ({ data }: { data: { conflicting_animals?: PatientData[] } }) => {
        if (data.conflicting_animals)
          this.setState(
            produce((draftState: PatientInfoFormState) => {
              (
                draftState[stateFieldKey as AutocompleteFieldKey] as CreatableFieldState
              ).conflictingAnimals = data.conflicting_animals;
            })
          );
      }
    );
  };

  getOptionsFromServer = (
    apiCall: (data: Record<string, unknown>) => Promise<{ data: Record<string, any[]> }>,
    responseKey: string,
    stateFieldKey: AutocompleteFieldKey,
    data: Record<string, unknown>
  ): Promise<void> =>
    apiCall(data)
      .then((response) => {
        const fieldState = this.state[stateFieldKey] as CreatableFieldState;
        const { backendField } = fieldState;
        const options: SelectOption[] = response.data[responseKey].map((value: any) => ({
          label: value[backendField],
          value: value[backendField],
          data: value,
        }));
        this.setState(
          produce((draftState: PatientInfoFormState) => {
            (draftState[stateFieldKey] as CreatableFieldState).options = options;
          })
        );
      })
      .catch((error) => {
        console.log('error', error);
      });

  loadOptions = (stateOptionsKey: AutocompleteFieldKey, data: Record<string, unknown>): void => {
    const fieldState = this.state[stateOptionsKey] as CreatableFieldState;
    const { getOptionsFromServer } = fieldState;

    getOptionsFromServer(stateOptionsKey, data);
  };

  makeRaceField = (specie?: SpeciesType, race?: string): RaceFieldState => {
    const { intl } = this.props;
    const raceField: RaceFieldState = {
      options: makeRacesOptions(intl, specie),
      selectedOption: null,
    };
    if (race && specie) {
      raceField.selectedOption = {
        label: intl.formatMessage({
          id: `patient_info.specie.${specie}.race.${race}`,
          defaultMessage: race,
        }),
        value: race,
      };
    }
    return raceField;
  };

  makeSpecieField = (specie?: SpeciesType): SelectOption<SpeciesType> | null => {
    const { intl } = this.props;
    return specie
      ? {
          value: specie,
          label: intl.formatMessage({
            id: `patient_info.specie.${specie}`,
            defaultMessage: specie,
          }),
        }
      : null;
  };

  makeSexField = (sex?: string): SelectOption<string> | null =>
    sex && SEXES.includes(sex)
      ? {
          value: sex,
          label: <i className={`svg-icon ${sex}`} />,
        }
      : null;

  onSelectSpecie = (
    value: ValueType<SelectOption<SpeciesType>, false>,
    { action }: ActionMeta<SelectOption<SpeciesType>>
  ): void => {
    if (!ON_CHANGE_ACTIONS.includes(action)) return;
    const defaultRace = _.get(value, 'value') === 'cat' ? DEFAULT_CAT_RACE : undefined;
    this.setState({
      specie: value,
      race: this.makeRaceField(value?.value, defaultRace),
    });
  };

  undoLastModification = (): void => this.setState(this.previousState);

  isFieldRequired = (fieldKey: PatientFieldKey): boolean => {
    const { fieldRules } = this.props;
    return !!fieldRules?.[fieldKey]?.required;
  };

  renderFileIdField = (isStateInConflicts: boolean): React.ReactNode => {
    const { intl } = this.props;
    const { fileID } = this.state;
    return (
      <FormField
        key="file_id"
        required={this.isFieldRequired('file_id')}
        disabled={isStateInConflicts}
        style={{ flex: '1 1 0px' }}
        error={this.props.fieldErrors?.file_id}
      >
        <label>
          <FormattedMessage id="patient_info.file_id" />
        </label>
        <Creatable
          createOptionPosition="first"
          isValidNewOption={(inputValue) => !!inputValue}
          styles={
            injectErrorInStyles(textSelectStyle, this.props.fieldErrors?.file_id) as StylesConfig<
              SelectOption,
              false
            >
          }
          value={fileID.selectedOption}
          inputValue={fileID.inputValue}
          placeholder={null}
          onFocus={fileID.onInputFocus}
          onInputChange={fileID.onInputChange}
          onChange={fileID.onSelect}
          options={fileID.options}
          menuPlacement={this.props.menuPlacement}
          components={fileID.components}
          isOptionSelected={isOptionDataSelected}
          isDisabled={isStateInConflicts}
          isClearable={!isStateInConflicts}
          formatCreateLabel={(inputValue) =>
            `${intl.formatMessage({ id: 'general.add' })} "${inputValue}"`
          }
        />
        {fileID.conflictingAnimals ? (
          <PatientConflictWarning
            cancel={this.undoLastModification}
            conflictingPatients={fileID.conflictingAnimals}
            selectCreate={this.solveConflictsForCreate}
            selectUpdate={this.solveConflictsForUpdate}
          />
        ) : null}
      </FormField>
    );
  };

  renderOwnerNameField = (isStateInConflicts: boolean): React.ReactNode => {
    const { intl } = this.props;
    const { owner } = this.state;
    return (
      <FormField
        key="owner_name"
        error={this.props.fieldErrors?.owner_name}
        required={this.isFieldRequired('owner_name')}
        disabled={isStateInConflicts}
        style={{ flex: '1 1 0px' }}
      >
        <label>
          <FormattedMessage id="patient_info.owner_name" />
        </label>
        <Creatable
          createOptionPosition="first"
          isValidNewOption={(inputValue) => !!inputValue}
          styles={
            injectErrorInStyles(
              textSelectStyle,
              this.props.fieldErrors?.owner_name
            ) as StylesConfig<SelectOption, false>
          }
          components={{ DropdownIndicator: ToolsDropdownIndicator, IndicatorSeparator: null }}
          value={owner.selectedOption}
          inputValue={owner.inputValue}
          placeholder={null}
          onFocus={owner.onInputFocus}
          onInputChange={owner.onInputChange}
          onChange={owner.onSelect}
          options={owner.options}
          menuPlacement={this.props.menuPlacement}
          isDisabled={isStateInConflicts}
          isClearable={!isStateInConflicts}
          formatCreateLabel={(inputValue) =>
            `${intl.formatMessage({ id: 'general.add' })} "${inputValue}"`
          }
        />
        {owner.conflictingAnimals ? (
          <PatientConflictWarning
            cancel={this.undoLastModification}
            conflictingPatients={owner.conflictingAnimals}
            selectCreate={this.solveConflictsForCreate}
            selectUpdate={this.solveConflictsForUpdate}
          />
        ) : null}
      </FormField>
    );
  };

  renderNameField = (isStateInConflicts: boolean): React.ReactNode => {
    const { intl } = this.props;
    const { animal } = this.state;
    return (
      <FormField
        key="name"
        error={this.props.fieldErrors?.name}
        required={this.isFieldRequired('name')}
        disabled={isStateInConflicts}
        style={{ flex: '1 1 0px' }}
      >
        <label>
          <FormattedMessage id="patient_info.animal_name" />
        </label>
        <Creatable
          createOptionPosition="first"
          isValidNewOption={(inputValue) => !!inputValue}
          styles={
            injectErrorInStyles(textSelectStyle, this.props.fieldErrors?.name) as StylesConfig<
              SelectOption,
              false
            >
          }
          value={animal.selectedOption}
          inputValue={animal.inputValue}
          placeholder={null}
          onFocus={animal.onInputFocus}
          onInputChange={animal.onInputChange}
          onChange={animal.onSelect}
          options={animal.options}
          menuPlacement={this.props.menuPlacement}
          components={animal.components}
          isOptionSelected={isOptionDataSelected}
          isDisabled={isStateInConflicts}
          isClearable={!isStateInConflicts}
          formatCreateLabel={(inputValue) =>
            `${intl.formatMessage({ id: 'general.add' })} "${inputValue}"`
          }
        />
        {animal.conflictingAnimals ? (
          <PatientConflictWarning
            cancel={this.undoLastModification}
            conflictingPatients={animal.conflictingAnimals}
            selectCreate={this.solveConflictsForCreate}
            selectUpdate={this.solveConflictsForUpdate}
          />
        ) : null}
      </FormField>
    );
  };

  renderChipIdField = (isStateInConflicts: boolean): React.ReactNode => {
    const { intl } = this.props;
    const { chipID } = this.state;
    return (
      <FormField
        key="chip_id"
        error={this.props.fieldErrors?.chip_id}
        required={this.isFieldRequired('chip_id')}
        disabled={isStateInConflicts}
        style={{ flex: '1 1 0px' }}
      >
        <label>
          <FormattedMessage id="patient_info.chip_id" />
        </label>
        <Creatable
          createOptionPosition="first"
          isValidNewOption={(inputValue) => !!inputValue}
          styles={
            injectErrorInStyles(textSelectStyle, this.props.fieldErrors?.chip_id) as StylesConfig<
              SelectOption,
              false
            >
          }
          value={chipID.selectedOption}
          inputValue={chipID.inputValue}
          placeholder={null}
          onFocus={chipID.onInputFocus}
          onInputChange={chipID.onInputChange}
          onChange={chipID.onSelect}
          options={chipID.options}
          menuPlacement={this.props.menuPlacement}
          components={chipID.components}
          isOptionSelected={isOptionDataSelected}
          isDisabled={isStateInConflicts}
          isClearable={!isStateInConflicts}
          formatCreateLabel={(inputValue) =>
            `${intl.formatMessage({ id: 'general.add' })} "${inputValue}"`
          }
        />
        {chipID.conflictingAnimals ? (
          <PatientConflictWarning
            cancel={this.undoLastModification}
            conflictingPatients={chipID.conflictingAnimals}
            selectCreate={this.solveConflictsForCreate}
            selectUpdate={this.solveConflictsForUpdate}
          />
        ) : null}
      </FormField>
    );
  };

  renderSpecieField = (isStateInConflicts: boolean): React.ReactNode => {
    const { intl } = this.props;
    const { specie } = this.state;
    return (
      <FormField
        key="specie"
        error={this.props.fieldErrors?.specie}
        required={this.isFieldRequired('specie')}
        disabled={isStateInConflicts}
        style={{ flex: '1 1 0px' }}
      >
        <label>
          <FormattedMessage id="patient_info.specie" />
        </label>
        <Creatable
          styles={
            injectErrorInStyles(textSelectStyle, this.props.fieldErrors?.specie) as StylesConfig<
              SelectOption<SpeciesType>,
              false
            >
          }
          value={specie}
          onChange={this.onSelectSpecie as any}
          getOptionValue={({ label }) => String(label)}
          components={{ DropdownIndicator: ToolsDropdownIndicator, IndicatorSeparator: null }}
          options={makeSpeciesOptions(intl) as SelectOption<SpeciesType>[]}
          menuPlacement={this.props.menuPlacement}
          isDisabled={isStateInConflicts}
          onBlur={(e) => {
            const inputValue = (e?.target as HTMLInputElement)?.value;
            if (inputValue) {
              this.onSelectSpecie(
                { value: inputValue, label: inputValue },
                { action: 'create-option' }
              );
            }
          }}
          isClearable={!isStateInConflicts}
          formatCreateLabel={(inputValue) =>
            `${intl.formatMessage({ id: 'general.add' })} "${inputValue}"`
          }
        />
      </FormField>
    );
  };

  renderBirthDateField = (isStateInConflicts: boolean): React.ReactNode => {
    const { intl } = this.props;
    const { birthDate } = this.state;
    return (
      <FormField
        key="birth_date"
        error={this.props.fieldErrors?.birth_date}
        required={this.isFieldRequired('birth_date')}
        disabled={isStateInConflicts}
        style={{ flex: '1 1 0px' }}
      >
        <label>
          <FormattedMessage id="patient_info.birth_date" />
        </label>
        <div className="date">
          <div className={`date-picker-wrapper ${isStateInConflicts ? 'disabled' : ''}`}>
            <ReactDatePicker
              dateFormat="P"
              locale={intl.locale}
              selected={birthDate.value}
              onChange={(value: Date | null) =>
                this.setState({ birthDate: { value: value || undefined } })
              }
              showYearDropdown
              scrollableYearDropdown
            />
          </div>
          <AgeFromDate birthDate={birthDate.value} />
        </div>
      </FormField>
    );
  };

  renderPedigreeIdField = (isStateInConflicts: boolean): React.ReactNode => {
    const { intl } = this.props;
    const { pedigreeID } = this.state;
    return (
      <FormField
        key="pedigree_id"
        error={this.props.fieldErrors?.pedigree_id}
        required={this.isFieldRequired('pedigree_id')}
        disabled={isStateInConflicts}
        style={{ flex: '1 1 0px' }}
      >
        <label>
          <FormattedMessage id="patient_info.pedigree_id" />
        </label>
        <Creatable
          isValidNewOption={(inputValue) => !!inputValue}
          styles={
            injectErrorInStyles(
              textSelectStyle,
              this.props.fieldErrors?.pedigree_id
            ) as StylesConfig<SelectOption, false>
          }
          value={pedigreeID.selectedOption}
          inputValue={pedigreeID.inputValue}
          placeholder={null}
          onFocus={pedigreeID.onInputFocus}
          onInputChange={pedigreeID.onInputChange}
          onChange={pedigreeID.onSelect}
          options={pedigreeID.options}
          menuPlacement={this.props.menuPlacement}
          components={pedigreeID.components}
          isOptionSelected={isOptionDataSelected}
          isDisabled={isStateInConflicts}
          isClearable={!isStateInConflicts}
          formatCreateLabel={(inputValue) =>
            `${intl.formatMessage({ id: 'general.add' })} "${inputValue}"`
          }
        />
      </FormField>
    );
  };

  renderRaceField = (isStateInConflicts: boolean): React.ReactNode => {
    const { intl } = this.props;
    const { specie, race } = this.state;
    return (
      <FormField
        key="race"
        error={this.props.fieldErrors?.race}
        required={this.isFieldRequired('race')}
        disabled={isStateInConflicts}
        style={{ flex: '1 1 0px' }}
      >
        <label>
          <FormattedMessage id="patient_info.race" />
        </label>
        <Creatable
          createOptionPosition="first"
          isValidNewOption={(inputValue) =>
            !!inputValue &&
            !_.some(race.options, ({ value }) => inputValue.toLowerCase() === value.toLowerCase())
          }
          styles={
            injectErrorInStyles(textSelectStyle, this.props.fieldErrors?.race) as StylesConfig<
              SelectOption,
              false
            >
          }
          value={race.selectedOption}
          placeholder={null}
          onChange={(value, { action }) =>
            ON_CHANGE_ACTIONS.includes(action) &&
            this.setState(
              produce((draftState: PatientInfoFormState) => {
                draftState.race.selectedOption = value;
              })
            )
          }
          getOptionValue={({ label }) => String(label)}
          components={{ DropdownIndicator: ToolsDropdownIndicator, IndicatorSeparator: null }}
          options={race.options}
          menuPlacement={this.props.menuPlacement}
          isDisabled={isStateInConflicts || !specie}
          isClearable={!isStateInConflicts}
          formatCreateLabel={(inputValue) =>
            `${intl.formatMessage({ id: 'general.add' })} "${inputValue}"`
          }
        />
      </FormField>
    );
  };

  renderSexField = (isStateInConflicts: boolean): React.ReactNode => {
    const { intl } = this.props;
    const { sex } = this.state;
    return (
      <FormField
        key="sex"
        error={this.props.fieldErrors?.sex}
        required={this.isFieldRequired('sex')}
        disabled={isStateInConflicts}
      >
        <label>
          <FormattedMessage id="patient_info.sex" />
        </label>
        <Select
          styles={
            injectErrorInStyles(sexStyles, this.props.fieldErrors?.sex) as StylesConfig<
              SelectOption,
              false
            >
          }
          value={sex}
          placeholder={null}
          onChange={(value, { action }) =>
            ON_CHANGE_ACTIONS.includes(action) && this.setState({ sex: value })
          }
          getOptionValue={({ label }) => String(label)}
          options={makeSexesOptions(intl) as SelectOption[]}
          menuPlacement={this.props.menuPlacement}
          components={{ DropdownIndicator: null, IndicatorSeparator: null }}
          isDisabled={isStateInConflicts}
        />
      </FormField>
    );
  };

  renderWeightField = (isStateInConflicts: boolean): React.ReactNode => {
    const { weight } = this.state;
    return (
      <FormField
        key="weight"
        error={this.props.fieldErrors?.weight}
        required={this.isFieldRequired('weight')}
        disabled={isStateInConflicts}
        style={{ flex: '1 1 0px' }}
      >
        <label>
          <FormattedMessage id="patient_info.weight" />
        </label>
        <Form.Input
          type="text"
          className="weight-input"
          value={weight || ''}
          onChange={(e) => this.setState({ weight: e.target.value || undefined })}
          disabled={isStateInConflicts}
          placeholder="kg"
        />
      </FormField>
    );
  };

  renderAttendingVeterinarianField = (isStateInConflicts: boolean): React.ReactNode => {
    const { attendingVeterinarian } = this.state;
    return (
      <FormField
        key="attending_veterinarian"
        error={this.props.fieldErrors?.attending_veterinarian}
        required={this.isFieldRequired('attending_veterinarian')}
        disabled={isStateInConflicts}
        style={{ flex: '1 1 0px' }}
      >
        <label>
          <FormattedMessage id="patient_info.attending_veterinarian" />
        </label>
        <AttendingVeterinarianSelector
          attendingVeterinarian={attendingVeterinarian}
          onChange={(value) =>
            this.setState(
              produce((draft: PatientInfoFormState) => {
                draft.attendingVeterinarian = value;
              })
            )
          }
        />
      </FormField>
    );
  };

  renderField = (fieldKey: PatientFieldKey, isStateInConflicts: boolean): React.ReactNode => {
    switch (fieldKey) {
      case 'file_id':
        return this.renderFileIdField(isStateInConflicts);
      case 'owner_name':
        return this.renderOwnerNameField(isStateInConflicts);
      case 'name':
        return this.renderNameField(isStateInConflicts);
      case 'chip_id':
        return this.renderChipIdField(isStateInConflicts);
      case 'specie':
        return this.renderSpecieField(isStateInConflicts);
      case 'birth_date':
        return this.renderBirthDateField(isStateInConflicts);
      case 'pedigree_id':
        return this.renderPedigreeIdField(isStateInConflicts);
      case 'race':
        return this.renderRaceField(isStateInConflicts);
      case 'sex':
        return this.renderSexField(isStateInConflicts);
      case 'weight':
        return this.renderWeightField(isStateInConflicts);
      case 'attending_veterinarian':
        return this.renderAttendingVeterinarianField(isStateInConflicts);
      default:
        return null;
    }
  };

  render(): React.ReactNode {
    const { className = '', fieldLayout = DEFAULT_FIELD_LAYOUT, fieldErrors } = this.props;
    const isStateInConflicts = hasConflicts(this.state);

    return (
      <div className={`patient-info-form ${className}`}>
        {fieldLayout.map((row, rowIndex) => (
          <div
            key={rowIndex}
            style={
              row.length > 1
                ? { display: 'flex', flexDirection: 'row', gap: '2em', flexGrow: 1 }
                : {}
            }
          >
            {row.map((fieldKey) =>
              Array.isArray(fieldKey) ? (
                <div
                  style={{ display: 'flex', flexDirection: 'row', gap: '1em', flex: '1 1 0px' }}
                  key={fieldKey.join(',')}
                >
                  {fieldKey.map((key) => this.renderField(key, isStateInConflicts))}
                </div>
              ) : (
                this.renderField(fieldKey, isStateInConflicts)
              )
            )}
          </div>
        ))}
      </div>
    );
  }
}

export default injectIntl(PatientInfoForm, { forwardRef: true });
